/**
 * LA FICHA DE ENCARGO DEL CHAT (CH-4 fase B, 27-sep-2026).
 *
 * El colaborador propone un trabajo de su oficio; el motor no lo lanza. Aquí se le pone el coste con
 * las mismas cuentas que la pantalla de ese trabajo (Diagnóstico, Plan, Pasadas, Corrección,
 * Publicación) y se lanza por el mismo camino, solo si el autor pulsa «Hazlo». Lo que decide queda
 * en el hilo. Encargado, la ficha sigue el trabajo y dice cuándo ha terminado.
 */
import { useEffect, useState } from "react";
import {
  catalogoModelos, costeDeLaPasada, faseDelLibro, dolares, encargos, gasto, informesDelLibro, lanzarPropuesta, marcarPropuesta, modelosAsignados, trabajos,
} from "../api";
import type { ModeloCatalogo, Trabajo } from "../api";
import { costeDePropuesta, fichaDeOtroManuscrito, nombreDeRuta } from "../logica";
import type { Propuesta } from "../logica";
import { useFlujo } from "../flujo";

// Una vez por pestaña: cada ficha no vuelve a pedir el catálogo ni los modelos asignados.
let preciosPedidos: Promise<{ porAgente: Record<string, string>; catalogo: ModeloCatalogo[] }> | null = null;
const precios = () => (preciosPedidos ??= Promise.all([modelosAsignados(), catalogoModelos()])
  .then(([a, c]) => ({ porAgente: a.porAgente ?? {}, catalogo: c.modelos ?? [] }))
  .catch((e) => { preciosPedidos = null; throw e; }));

export function PropuestaEncargo({ p, agente, carpeta, doc, alCambiar, alLanzar }: {
  p: Propuesta;
  agente: string;                  // el colaborador de la conversación (a quien pertenece el hilo)
  carpeta: string;
  doc: string | null;              // el manuscrito abierto: sobre él se lanza
  alCambiar: (p: Propuesta) => void;
  alLanzar: () => void;            // que las pantallas y la barra de trabajos se enteren
}) {
  const flujo = useFlujo();
  const [coste, setCoste] = useState<number | null | undefined>(undefined);
  const [yaHecho, setYaHecho] = useState(false);   // hay uno de antes: «Hazlo» pide otro, como «Rehacer»
  const [lanzando, setLanzando] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const [trabajo, setTrabajo] = useState<Trabajo | null>(null);
  const [perdido, setPerdido] = useState(false);
  // La ficha es del manuscrito del que habló el colaborador (equipo rojo 1.6.28): con otro abierto no
  // se lanza, porque el coste, el alcance y el porqué eran de aquel.
  const otroDoc = fichaDeOtroManuscrito(p.version, doc);

  // El coste, antes de pulsar. Sin precio o sin el dato, se calla en vez de inventar una cifra.
  useEffect(() => {
    if (p.estado !== "propuesta") return;
    let vigente = true;
    (async () => {
      try {
        const { porAgente, catalogo } = await precios();
        const modelo = catalogo.find((m) => m.id === porAgente[p.agente]);
        const palabrasPasada = p.forma === "pasada" && doc ? (await costeDeLaPasada(carpeta, doc, null, p.pasada ?? null)).palabras
          // La fase entera: lo que el motor dice que van a mandar sus pasadas (M-5).
          : p.forma === "fase" && doc ? (await faseDelLibro(carpeta, doc, p.fase ?? null))?.palabras ?? null
          : null;
        const delCatalogo = p.forma === "encargo" && p.encargo ? (await encargos(carpeta, doc))?.find((e) => e.id === p.encargo) ?? null : null;
        // El informe de lectura y el plan: lo que costaron de media en ESTE libro, según la bitácora.
        const suma = p.forma === "medida" && p.op ? (await gasto(carpeta).catch(() => null))?.porOp?.[p.op] : null;
        const medido = suma && suma.ops > 0 ? suma.coste / suma.ops : null;
        if (p.informe) setYaHecho(!!(await informesDelLibro(carpeta).catch(() => null))?.[p.informe]);
        if (delCatalogo?.hecho) setYaHecho(true);
        if (vigente) setCoste(costeDePropuesta(p, modelo, { palabrasPasada, caracteresEncargo: delCatalogo?.caracteres ?? null, medido }));
      } catch { if (vigente) setCoste(null); }
    })();
    return () => { vigente = false; };
  }, [p.id, p.estado, carpeta, doc]);

  // Encargada: se sigue el trabajo hasta que termina, sin que el autor tenga que ir a buscarlo. Si
  // tres veces seguidas no aparece (motor reiniciado, trabajo viejo), se deja de mirar y se dice.
  useEffect(() => {
    if (p.estado !== "encargado" || !p.trabajo) return;
    let vigente = true, fallos = 0, reloj: ReturnType<typeof setTimeout> | null = null;
    const mirar = async () => {
      const lista = await trabajos(carpeta).catch(() => null);
      const t = lista?.find((x) => x.id === p.trabajo) ?? null;
      if (!vigente) return;
      if (t) { setTrabajo(t); fallos = 0; } else fallos++;
      if (fallos >= 3) { setPerdido(true); return; }
      if (!t || t.estado === "en_curso") reloj = setTimeout(mirar, 8000);
    };
    mirar();
    return () => { vigente = false; if (reloj) clearTimeout(reloj); };
  }, [p.estado, p.trabajo, carpeta]);

  const decidir = async (estado: "encargado" | "descartado", idTrabajo: string | null = null) => {
    const nueva = { ...p, estado, trabajo: idTrabajo };
    alCambiar(nueva);
    // Si no se puede apuntar en el hilo, se dice: al volver, la ficha podría ofrecer «Hazlo» otra vez.
    try { await marcarPropuesta(carpeta, agente, p.id, estado, idTrabajo); }
    catch (e) {
      if ((e as { codigo?: string }).codigo === "ya_decidida") return;
      setFallo(estado === "encargado"
        ? "Encargado, pero no he podido apuntarlo en la conversación: si al volver la ficha sigue ofreciéndolo, no lo pidas otra vez."
        : "No he podido apuntarlo en la conversación.");
    }
  };

  const hazlo = async () => {
    if (!doc) { setFallo("Elige antes tu manuscrito en Proyectos: el trabajo se hace sobre él."); return; }
    if (otroDoc) return;
    setLanzando(true);
    setFallo(null);
    try {
      const t = await lanzarPropuesta(p, carpeta, doc, { rehacer: yaHecho, libre: flujo });
      if (t) setTrabajo(t);
      await decidir("encargado", t?.id ?? null);
      alLanzar();
    } catch (e) {
      setFallo(e instanceof Error ? e.message : String(e));
    } finally {
      setLanzando(false);
    }
  };

  if (p.estado === "descartado") {
    return <span className="propuesta-encargo descartada"><span>Descartaste «{p.titulo}».</span></span>;
  }
  const encargado = p.estado === "encargado";
  const cifra = coste != null ? `~${dolares(coste)}` : null;
  const seguimiento = !encargado ? null
    : !trabajo ? "✓ Encargado. " + p.deja
    : trabajo.estado === "en_curso" ? `En marcha${trabajo.progreso ? ` (${trabajo.progreso.hecho} de ${trabajo.progreso.total})` : ""}. ${p.deja} Te lo digo aquí cuando termine.`
    : trabajo.estado === "hecho" ? "✓ Terminado. " + p.deja
    : `No ha salido: ${trabajo.error ?? "el trabajo se cortó"}.`;
  const lineaCoste = cifra
    ? `${cifra} de tu clave${p.forma === "medida" ? " (lo que costó de media en este libro)" : ""} · `
    : coste === null
      ? (p.forma === "medida" ? "Coste: no lo sé antes de hacerlo (lee el libro capítulo a capítulo); lo verás en Gasto · " : "Coste: no lo sé con este modelo · ")
      : "";

  return (
    <span className="propuesta-encargo">
      <span className="grado alta">{encargado ? "ENCARGADO" : "TE PROPONE UN ENCARGO"}</span>
      <span className="titulo">{p.titulo}</span>
      <span>{p.alcance}</span>
      {p.porque && <span>Por qué: {p.porque}</span>}
      {encargado
        ? <span>{perdido ? "Encargado, pero ya no lo encuentro en la lista de trabajos: míralo arriba, en la barra de trabajos." : seguimiento}</span>
        : <span>{lineaCoste}{yaHecho ? "Ya hay uno de antes: este será uno nuevo. " : ""}{p.deja}</span>}
      {otroDoc && !encargado && (
        <span className="fallo-propuesta">Esta ficha es sobre «{p.version}» y ahora tienes abierto «{nombreDeRuta(doc ?? "")}». Si lo quieres para este, pídeselo otra vez.</span>
      )}
      {fallo && <span className="fallo-propuesta">{fallo}</span>}
      {!encargado && (
        <span className="botones">
          <button className="btn" disabled={lanzando || coste === undefined || otroDoc} onClick={hazlo}>
            {lanzando ? "Encargando…" : cifra ? `Hazlo · ${cifra}` : "Hazlo"}
          </button>
          <button className="btn texto" disabled={lanzando} onClick={() => decidir("descartado")}>Ahora no</button>
        </span>
      )}
    </span>
  );
}
